import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Button } from 'semantic-ui-react';
import { v4 as uuidv4 } from 'uuid';
import { CategoryOption } from '../api/categoryApi';
import { ColorPicker, generateDistinctColor } from 'common/components/ColorPicker';

interface CategoryOptionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (option: CategoryOption) => void;
  initialOption?: CategoryOption | null;
  existingColors?: string[];
}

const defaultOption: CategoryOption = {
  id: '',
  name: '',
  color: '#000000',
  description: '',
  fr: '',
};

const CategoryOptionModal: React.FC<CategoryOptionModalProps> = ({
  open,
  onClose,
  onSave,
  initialOption,
  existingColors = [],
}) => {
  const [optionState, setOptionState] = useState<CategoryOption>(defaultOption);

  // `existingColors` is read, never reacted to. Callers pass an inline
  // `.map(...)`, so it is a fresh array on every parent render; depending on it
  // re-seeded the form (new uuid, new color) on any parent re-render while the
  // modal was open, and with the `= []` default it re-seeded on every render of
  // this component -- an unbounded render loop.
  const existingColorsRef = useRef(existingColors);
  existingColorsRef.current = existingColors;

  useEffect(() => {
    if (initialOption) {
      setOptionState(initialOption);
    } else {
      setOptionState({
        ...defaultOption,
        id: uuidv4(),
        color: generateDistinctColor(existingColorsRef.current)
      });
    }
  }, [initialOption, open]);

  const handleChange = (field: keyof CategoryOption, value: string) => {
    setOptionState(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (closeAfter: boolean) => {
    if (!optionState.name.trim()) return;
    onSave(optionState);
    if (closeAfter) {
      onClose();
    } else {
      // Reset for a new entry
      setOptionState({ ...defaultOption, id: uuidv4() });
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>{initialOption ? 'Edit Option' : 'Add Option'}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            label="Name"
            value={optionState.name}
            onChange={(_, { value }) => handleChange('name', value)}
            required
          />
          <ColorPicker
            color={optionState.color}
            onChange={(value: string) => handleChange('color', value)}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
        {!initialOption && (
          <Button primary onClick={() => handleSubmit(false)}>Add Another</Button>
        )}
        <Button primary onClick={() => handleSubmit(true)}>
          {initialOption ? 'Update' : 'Add'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CategoryOptionModal;
